import { useEffect, useRef, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { startSession, endSession } from "../Redux/sessionSlice";
import secureLocalStorage from "react-secure-storage";

const IDLE_TIMEOUT = 30 * 60 * 1000;
const WARNING_DURATION = 2 * 60 * 1000;
const WARNING_THRESHOLD = IDLE_TIMEOUT - WARNING_DURATION;

const DEBUG_IDLE = true;

function formatTime(ms) {
  const safeMs = Math.max(0, ms);
  const totalSeconds = Math.floor(safeMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

export const useIdleTimer = (onTimeout, onWarning, options = {}) => {
  const isActive = useSelector((state) => state?.userSession?.isActive);
  const dispatch = useDispatch();

  const { trackingPausedRef, onResetRef } = options;

  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const sessionEndedRef = useRef(false);

  const warningTimerRef = useRef(null);
  const timeoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const warningDeadlineRef = useRef(null);

  const onTimeoutRef = useRef(onTimeout);
  const onWarningRef = useRef(onWarning);

  const [warningSecondsRemaining, setWarningSecondsRemaining] = useState(
    Math.ceil(WARNING_DURATION / 1000),
  );

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    onWarningRef.current = onWarning;
  }, [onWarning]);

  const clearCountdownInterval = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }

    clearCountdownInterval();
  }, [clearCountdownInterval]);

  const updateCountdown = useCallback(() => {
    if (!warningDeadlineRef.current) {
      setWarningSecondsRemaining(Math.ceil(WARNING_DURATION / 1000));
      return;
    }

    const msRemaining = warningDeadlineRef.current - Date.now();
    const secondsRemaining = Math.max(0, Math.ceil(msRemaining / 1000));

    setWarningSecondsRemaining(secondsRemaining);

    if (DEBUG_IDLE && warningShownRef.current) {
      console.log("[IdleTimer] Countdown update:", {
        secondsRemaining,
        formatted: formatTime(msRemaining),
      });
    }
  }, []);

  const startCountdown = useCallback(
    (msRemaining) => {
      clearCountdownInterval();

      warningDeadlineRef.current = Date.now() + msRemaining;
      updateCountdown();

      if (DEBUG_IDLE) {
        console.log("[IdleTimer] Starting countdown:", {
          msRemaining,
          formatted: formatTime(msRemaining),
        });
      }

      countdownIntervalRef.current = setInterval(() => {
        updateCountdown();
      }, 1000);
    },
    [clearCountdownInterval, updateCountdown],
  );

  const stopCountdown = useCallback(() => {
    warningDeadlineRef.current = null;
    setWarningSecondsRemaining(Math.ceil(WARNING_DURATION / 1000));
    clearCountdownInterval();

    if (DEBUG_IDLE) {
      console.log("[IdleTimer] Countdown stopped/reset");
    }
  }, [clearCountdownInterval]);

  const endSessionNow = useCallback(() => {
    if (sessionEndedRef.current) return;

    if (DEBUG_IDLE) {
      console.log("⛔ [IdleTimer] Timeout triggered");
    }

    sessionEndedRef.current = true;
    warningShownRef.current = false;
    stopCountdown();
    clearTimers();

    onTimeoutRef.current?.();
  }, [clearTimers, stopCountdown]);

  const showWarningNow = useCallback(
    (msRemaining = WARNING_DURATION) => {
      if (sessionEndedRef.current || warningShownRef.current) return;

      if (DEBUG_IDLE) {
        console.log("⚠️ [IdleTimer] Warning triggered", {
          msRemaining,
          formatted: formatTime(msRemaining),
        });
      }

      warningShownRef.current = true;
      startCountdown(msRemaining);
      onWarningRef.current?.();
    },
    [startCountdown],
  );

  const scheduleTimers = useCallback(() => {
    clearTimers();

    const now = Date.now();
    const elapsed = now - lastActivityRef.current;
    const remainingUntilWarning = WARNING_THRESHOLD - elapsed;
    const remainingUntilTimeout = IDLE_TIMEOUT - elapsed;

    if (DEBUG_IDLE) {
      console.log("------ IdleTimer State ------");
      console.log("Elapsed since last activity:", formatTime(elapsed));
      console.log("Time until warning:", formatTime(remainingUntilWarning));
      console.log("Time until timeout:", formatTime(remainingUntilTimeout));
      console.log(
        "Warning should be visible:",
        elapsed >= WARNING_THRESHOLD && elapsed < IDLE_TIMEOUT,
      );
      console.log("Timeout should happen:", elapsed >= IDLE_TIMEOUT);
      console.log("trackingPausedRef.current:", trackingPausedRef?.current);
      console.log("isActive:", isActive);
      console.log("hasToken:", !!secureLocalStorage.getItem("token"));
      console.log("-----------------------------");
    }

    if (elapsed >= IDLE_TIMEOUT) {
      endSessionNow();
      return;
    }

    if (elapsed >= WARNING_THRESHOLD) {
      const remainingTime = IDLE_TIMEOUT - elapsed;

      showWarningNow(remainingTime);

      timeoutTimerRef.current = setTimeout(() => {
        endSessionNow();
      }, remainingTime);

      if (DEBUG_IDLE) {
        console.log(
          "[IdleTimer] Already in warning window, timeout scheduled for:",
          {
            msRemaining: remainingTime,
            formatted: formatTime(remainingTime),
          },
        );
      }

      return;
    }

    warningTimerRef.current = setTimeout(() => {
      showWarningNow(WARNING_DURATION);

      timeoutTimerRef.current = setTimeout(() => {
        endSessionNow();
      }, WARNING_DURATION);

      if (DEBUG_IDLE) {
        console.log("[IdleTimer] Warning shown, timeout scheduled for:", {
          msRemaining: WARNING_DURATION,
          formatted: formatTime(WARNING_DURATION),
        });
      }
    }, WARNING_THRESHOLD - elapsed);

    if (DEBUG_IDLE) {
      console.log("[IdleTimer] Warning scheduled for:", {
        msRemaining: WARNING_THRESHOLD - elapsed,
        formatted: formatTime(WARNING_THRESHOLD - elapsed),
      });
    }
  }, [clearTimers, endSessionNow, showWarningNow, trackingPausedRef, isActive]);

  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
    sessionEndedRef.current = false;
    stopCountdown();

    if (DEBUG_IDLE) {
      console.log(
        "🔄 [IdleTimer] Timers reset at:",
        new Date().toLocaleTimeString(),
      );
    }

    scheduleTimers();
  }, [scheduleTimers, stopCountdown]);

  const handleActivity = useCallback(() => {
    if (trackingPausedRef?.current) {
      if (DEBUG_IDLE) {
        console.log("[IdleTimer] Activity ignored because tracking is paused");
      }
      return;
    }

    if (DEBUG_IDLE) {
      console.log("👆 [IdleTimer] User activity detected, resetting timers");
    }

    resetTimers();
  }, [resetTimers, trackingPausedRef]);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState !== "visible") return;

    if (DEBUG_IDLE) {
      console.log("👀 [IdleTimer] Tab became visible, recalculating timers");
    }

    // Keep Redux session state in sync if a token exists or disappears,
    // but do not stop the idle timer for anonymous users.
    const token = secureLocalStorage.getItem("token");

    if (token && !isActive) {
      dispatch(startSession());
    } else if (!token && isActive) {
      dispatch(endSession());
    }

    scheduleTimers();
    updateCountdown();
  }, [dispatch, isActive, scheduleTimers, updateCountdown]);

  useEffect(() => {
    const token = secureLocalStorage.getItem("token");

    if (DEBUG_IDLE) {
      console.log("[IdleTimer] Mount/effect run", {
        isActive,
        hasToken: !!token,
      });
    }

    // Sync auth state, but do not gate timer startup on it.
    if (token && !isActive) {
      dispatch(startSession());
    } else if (!token && isActive) {
      dispatch(endSession());
    }

    resetTimers();

    if (onResetRef) {
      onResetRef.current = resetTimers;
    }

    window.debugIdleReset = resetTimers;

    const events = ["mousemove", "mousedown", "keydown", "touchstart"];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      document.removeEventListener("visibilitychange", handleVisibilityChange);

      clearTimers();
      stopCountdown();

      if (DEBUG_IDLE) {
        console.log("[IdleTimer] Cleanup complete");
      }
    };
  }, [
    dispatch,
    isActive,
    resetTimers,
    handleActivity,
    handleVisibilityChange,
    clearTimers,
    stopCountdown,
    onResetRef,
  ]);

  return {
    warningSecondsRemaining,
  };
};
